import { OrderDetailLineInfo, OrderInfo } from '~/api/orders/_types';
import { FilterCardColumnConfig, FilterDataType } from '~/components/base/filters/_types';
import { AppTableColumnConfig } from '~/components/base/tables/_types';
import { t } from '~/plugins/i18n';
import { formatNumber } from '~/utils/localeUtils';

export const getOrdersOverviewColumnConfig = (filter?: string[]): AppTableColumnConfig[] => {
    const columns: AppTableColumnConfig[] = [
        {
            name: 'DocumentNumber',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.documentNumber' },
            field: 'DocumentNumber',
            align: 'left',
            sortable: true,
            classes: 'text-bold',
        },
        {
            name: 'ReferenceNumber',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.referenceNumber' },
            field: 'ReferenceNumber',
            align: 'left',
            sortable: true,
        },
        {
            name: 'PartnerName',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.partner' },
            field: (val) => val.Partner?.CardName,
            align: 'left',
            sortable: true,
        },
        {
            name: 'ContactName',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.contact' },
            field: (val) => val.Contact?.FullName,
            align: 'left',
            sortable: true,
        },
        {
            name: 'CreatedAt',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.createdAt.eq' },
            field: 'CreatedAt',
            align: 'left',
            sortable: true,
        },
        {
            name: 'RequiredOrShipAt',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.requiredOrShipAt.eq' },
            field: 'RequiredOrShipAt',
            align: 'left',
            sortable: true,
        },
        {
            name: 'PriceTotal',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.price' },
            field: (val: OrderInfo) =>
                val.Price ? formatNumber(val.Price?.Value) + ' ' + t(`generic.currency.${val.Price.Currency}`) : null,
            align: 'left',
            sortable: true,
        },
        {
            name: 'Status',
            label: '',
            translatedLabel: { key: 'orders.overview.columns.status' },
            field: 'Status',
            align: 'left',
            sortable: true,
        },
        {
            name: 'Actions',
            label: '',
            field: '',
            align: 'right',
        },
    ];

    return filter == null ? columns : columns.filter((x) => !filter.find((y) => x.name === y));
};

export const getOrdersOverviewFilterColumnConfig = (): FilterCardColumnConfig[] => [
    {
        dataType: FilterDataType.String,
        key: 'DocumentNumber',
        label: 'orders.overview.columns.documentNumber',
        additionalFilter: false,
    },
    {
        dataType: FilterDataType.String,
        key: 'ReferenceNumber',
        label: 'orders.overview.columns.referenceNumber',
        additionalFilter: false,
    },
    {
        dataType: FilterDataType.Enum,
        key: 'PartnerId',
        label: 'orders.overview.columns.partner',
        additionalFilter: false,
        multiple: true,
        autocomplete: true,
        optionsKey: 'Id',
        optionsLabelKey: 'CardName',
    },
    {
        dataType: FilterDataType.Enum,
        key: 'ContactId',
        label: 'orders.overview.columns.contact',
        additionalFilter: false,
        multiple: true,
        autocomplete: true,
        optionsKey: 'Id',
        optionsLabelKey: 'FullName',
    },
    {
        dataType: FilterDataType.Date,
        mode: 'range',
        key: 'CreatedAt',
        labelFrom: 'orders.overview.columns.createdAt.from',
        labelTo: 'orders.overview.columns.createdAt.to',
        additionalFilter: false,
    },
    {
        dataType: FilterDataType.Date,
        mode: 'range',
        key: 'RequiredOrShipAt',
        labelFrom: 'orders.overview.columns.requiredOrShipAt.from',
        labelTo: 'orders.overview.columns.requiredOrShipAt.to',
        additionalFilter: false,
    },
    {
        dataType: FilterDataType.Enum,
        key: 'Status',
        label: 'orders.overview.columns.status',
        additionalFilter: false,
        multiple: true,
    },
];

export const getOrderLinesDetailColumnConfig = (filter?: string[]): AppTableColumnConfig[] => {
    const columns: AppTableColumnConfig[] = [
        {
            name: 'Item',
            label: '',
            translatedLabel: { key: 'orders.detail.lines.columns.item' },
            field: 'Item',
            align: 'left',
        },
        {
            name: 'Nomenclature',
            label: '',
            translatedLabel: { key: 'orders.detail.lines.columns.nomenclature' },
            field: 'Nomenclature',
            align: 'left',
        },
        {
            name: 'ShipAt',
            label: '',
            translatedLabel: { key: 'orders.detail.lines.columns.shipAt' },
            field: (val: OrderDetailLineInfo) => val.RequiredOrShipAt?.toLocaleString(),
            align: 'center',
        },
        {
            name: 'Status',
            label: '',
            translatedLabel: { key: 'orders.detail.lines.columns.status' },
            field: 'Status',
            align: 'left',
        },
        {
            name: 'Quantity',
            label: '',
            translatedLabel: { key: 'orders.detail.lines.columns.quantity' },
            field: (val: OrderDetailLineInfo) => `${val.Quantity}x`,
            align: 'center',
        },
        {
            name: 'PriceDistributor',
            label: '',
            translatedLabel: { key: 'orders.detail.lines.columns.distributorPrice' },
            field: 'PriceDistributor',
            align: 'right',
        },
    ];

    return filter == null ? columns : columns.filter((x) => !filter.find((y) => x.name === y));
};

export const getOrderFormItemsColumnConfig = (filter?: string[]): AppTableColumnConfig[] => {
    const columns: AppTableColumnConfig[] = [
        {
            name: 'Actions',
            label: '',
            field: '',
            align: 'left',
            headerClasses: 'bg-background',
        },
        {
            name: 'Code',
            label: '',
            translatedLabel: { key: 'orders.form.items.columns.Code' },
            field: 'Code',
            align: 'left',
            classes: 'text-bold',
        },
        {
            name: 'Name',
            label: '',
            translatedLabel: { key: 'orders.form.items.columns.Name' },
            field: 'Name',
            align: 'left',
        },
        {
            name: 'Nomenclature',
            label: '',
            translatedLabel: { key: 'orders.form.items.columns.Nomenclature' },
            field: 'Nomenclature',
            align: 'right',
        },
        {
            name: 'Quantity',
            label: '',
            translatedLabel: { key: 'orders.form.items.columns.Quantity' },
            field: 'Quantity',
            align: 'left',
        },
        {
            name: 'RequiredAt',
            label: '',
            translatedLabel: { key: 'orders.form.items.columns.RequiredAt' },
            field: 'RequiredAt',
            align: 'left',
        },
        {
            name: 'PriceCatalogue',
            label: '',
            translatedLabel: { key: 'orders.form.items.columns.PriceCatalogue' },
            field: 'PriceCatalogue',
            align: 'right',
        },
        {
            name: 'PriceDistributor',
            label: '',
            translatedLabel: { key: 'orders.form.items.columns.PriceDistributor' },
            field: 'PriceDistributor',
            align: 'right',
        },
    ];

    return filter == null ? columns : columns.filter((x) => !filter.find((y) => x.name === y));
};
